/**
 * COLOR PICKER — il selettore di colore dell'applicazione: area colore e tonalità di
 * react-colorful in un popover con «Annulla» e «OK». Prende il posto del selettore nativo del
 * browser, che non si chiude se non cliccando fuori e non ha un modo di rinunciare alla scelta.
 *
 * Due pezzi: `ColorPickerPopover`, il popover da solo, per chi ha già un suo pulsante (la toolbar
 * dell'editor); `ColorPicker`, la pastiglia col colore corrente che lo apre (colori aziendali,
 * zone, colori commerciali).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Popover from '@mui/material/Popover';
import Button from '@mui/material/Button';
import { HexColorPicker } from 'react-colorful';

const HEX6 = /^#[0-9a-f]{6}$/i;

/** Il colore da cui parte il selettore quando il valore non è un `#rrggbb`. */
const DEFAULT_COLOR = '#000000';

export function ColorPickerPopover({
  anchorEl,
  value,
  onConfirm,
  onClose,
}: {
  anchorEl: HTMLElement | null;
  /** Colore di partenza; se non è un `#rrggbb` si parte dal nero. */
  value: string;
  /** Richiamato con il colore scelto, alla pressione di «OK». */
  onConfirm: (hex: string) => void;
  /** Richiamato su «Annulla» e sul clic fuori: la scelta si perde. */
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(DEFAULT_COLOR);

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      // Ogni apertura riparte dal valore corrente: una scelta annullata non resta in bozza.
      slotProps={{ transition: { onEnter: () => setDraft(HEX6.test(value) ? value : DEFAULT_COLOR) } }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <div className="color-picker__panel">
        <HexColorPicker color={draft} onChange={setDraft} />
        <div className="color-picker__actions">
          <Button size="small" onClick={onClose}>{t('common.cancel')}</Button>
          <Button size="small" variant="contained" onClick={() => onConfirm(draft)}>
            {t('common.ok')}
          </Button>
        </div>
      </div>
    </Popover>
  );
}

export function ColorPicker({
  label,
  value,
  onChange,
  disabled = false,
  className,
}: {
  /** Etichetta accessibile della pastiglia. */
  label: string;
  /** Colore corrente; se non è un `#rrggbb` la pastiglia lo mostra come nero. */
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  /** Classe in più sulla pastiglia, per chi deve segnalare uno stato (es. colore non impostato). */
  className?: string;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <button
        type="button"
        className={`color-picker${className ? ` ${className}` : ''}`}
        aria-label={label}
        title={label}
        disabled={disabled}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {/* Il colore è un dato, non uno stile del tema: lo porta `fill`, che è un attributo. */}
        <svg className="color-picker__fill" aria-hidden="true">
          <rect width="100%" height="100%" fill={HEX6.test(value) ? value : DEFAULT_COLOR} />
        </svg>
      </button>
      <ColorPickerPopover
        anchorEl={anchorEl}
        value={value}
        onConfirm={(hex) => {
          setAnchorEl(null);
          onChange(hex);
        }}
        onClose={() => setAnchorEl(null)}
      />
    </>
  );
}
